import { destinationName, DESTINATIONS, readTravel, TRAVEL_TYPES, type TravelType } from './travel';

/**
 * The flight tracker (website roadmap S23 — FF Scouter premium's flight tracker): where a player flew, when they took off,
 * when they land for each way of flying, and whether they usually fly faster than standard (an airstrip, a private jet or
 * a business class ticket). One copy for the server (apps/api/src/flights.ts keeps the flights), the website (player page
 * and war room) and the tests. Flight times: travel.ts (DESTINATIONS × TRAVEL_TYPES).
 *
 * How a flight is timed: Omni-Torn only sees a player now and then (a faction watch, a page a user viewed, a list a user's
 * key read). Each sighting puts them somewhere — in Torn, flying out to X, abroad in X, flying back from X. A flight (one
 * leg) is: when they were last seen somewhere else before it (they took off after that), first and last seen on it, and
 * first seen somewhere else after it (they landed by then). For each way of flying, the take-off lies between "after" and
 * "first seen flying" (and no earlier than one flight before first seen), the landing one flight later — and after "last
 * seen flying" and before "landed by". A way whose window comes out empty can't be how they flew: landing before standard
 * could have = a faster plane; still flying after business class would have landed = not business.
 *
 * Fed (terms FLIGHTS_TERMS) by: the player database's status/travel sightings from senders on 11 (never about the sender
 * themself), the activity watch's faction member lists (a bot key whose owner agreed to 11) and the Discord bot's live
 * view of a faction (only when every working bot key of that faction belongs to a member on 11). Coverage is what those
 * see — nowhere near FF Scouter's volunteer scanners.
 */

/** From this terms version on, other players' flights are kept and shown to Omni-Torn users (key-terms.ts, at merge). */
export const FLIGHTS_TERMS = 11;
/** Flights are kept this many days (from the last time the flight was seen). */
export const FLIGHTS_KEEP_DAYS = 90;
/** Finished flights kept per player (the current one comes on top). */
export const FLIGHTS_KEEP_LEGS = 10;
/** GET /api/flights?ids= — at most this many players at once (a war room's enemy roster). */
export const FLIGHTS_MAX_IDS = 100;
/** A sighting older than this when it reaches the server is too old to time a flight with: skipped. */
export const FLIGHTS_MAX_AGE_MS = 24 * 3_600_000;
/** Slack for clocks and Torn's rounding when ruling a way of flying out. */
export const FLIGHTS_SLACK_MS = 2 * 60_000;
export const FLIGHTS_PATH = '/api/flights';
export const FLIGHTS_PLAYER_PATH = '/api/flights/player';

export type FlightLegDir = 'out' | 'back';
/** Where a sighting puts a player: 'torn', 'there:Japan' (abroad), 'out:Japan' (flying there), 'back:Japan' (flying home). */
export type FlightPlace = string;

export const TRAVEL_TYPE_ORDER: readonly TravelType[] = ['standard', 'airstrip', 'wlt', 'business'];
export const TRAVEL_TYPE_LABEL: Record<TravelType, string> = { standard: 'Standard', airstrip: 'Airstrip', wlt: 'Private jet', business: 'Business class' };
export const isTravelType = (x: unknown): x is TravelType => typeof x === 'string' && (TRAVEL_TYPE_ORDER as readonly string[]).includes(x);

/** One flight (one leg) as kept and sent. Times in ms. */
export interface FlightLeg {
  destination: string;
  leg: FlightLegDir;
  /** Last seen somewhere else before it — they took off after this (null: not seen before it). */
  afterAt: number | null;
  /** First and last seen on it. */
  firstAt: number;
  lastAt: number;
  /** First seen somewhere else after it — they had landed by then (null: not seen since). */
  landedAt: number | null;
  /** The plane, when Torn's page named it. */
  plane: TravelType | null;
}

/** One way of flying for one flight: could it be how they flew, and the take-off and landing windows if so. */
export interface MethodWindow {
  type: TravelType;
  possible: boolean;
  /** One-way flight time (ms). */
  flightMs: number;
  departEarliest: number;
  departLatest: number;
  landEarliest: number;
  landLatest: number;
}

/** What a player's flights say about how they usually fly (only flights whose timing ruled a way in or out count). */
export interface PlaneGuess {
  /** The ways of flying that fit the most timed flights (slowest first). */
  types: TravelType[];
  /** Flights whose timing told something, and of those, how many were too quick for standard. */
  timed: number;
  fast: number;
  /** "Airstrip/business likely": most timed flights were too quick for standard. */
  fastLikely: boolean;
}

/** GET /api/flights/player/:id. */
export interface FlightPlayerAnswer {
  id: number;
  at: number;
  /** Days kept; 0 = no end date (terms KEEP_ALL_TERMS, "No age limits"). */
  keepDays: number;
  /** The flight they're on (seen on it, and standard's latest landing isn't past). */
  current: FlightLeg | null;
  /** Last seen abroad (and since when). */
  abroad: { destination: string; since: number; seenAt: number } | null;
  /** Earlier flights, newest first (≤ FLIGHTS_KEEP_LEGS). */
  recent: FlightLeg[];
  guess: PlaneGuess | null;
}

/** GET /api/flights?ids= — each player Omni-Torn has a flight or a stay abroad for (others left out). */
export interface FlightsAnswer {
  at: number;
  players: Array<{ id: number; current: FlightLeg | null; abroad: FlightPlayerAnswer['abroad']; guess: PlaneGuess | null }>;
}

// ---------------------------------------------------------------- where a sighting puts a player

/** Torn's status (state + description) → where they are, or null when it can't be told (an abroad hospital, Fallen…). */
export function placeOfStatus(state: unknown, description: unknown): FlightPlace | null {
  const s = typeof state === 'string' ? state.trim().toLowerCase() : '';
  const d = typeof description === 'string' ? description.trim() : '';
  if (s === 'traveling' || s === 'travelling') {
    const t = readTravel(d);
    return t && t.leg !== 'there' ? `${t.leg}:${t.destination}` : null;
  }
  if (s === 'abroad') {
    const t = readTravel(d);
    return t && t.leg === 'there' ? `there:${t.destination}` : null;
  }
  if (s === 'okay' || s === 'jail' || s === 'federal') return 'torn';
  // "In hospital for 2 hrs" is Torn's; "In a Swiss hospital for 2 hrs" is abroad (which country isn't always clear).
  if (s === 'hospital') return !d || /^(in )?hospital\b/i.test(d) ? 'torn' : null;
  return null;
}

/** A 'travel' observation's data ({ destination, leg }) → where they are, or null. */
export function placeOfTravel(data: Record<string, unknown>): FlightPlace | null {
  const dest = typeof data.destination === 'string' ? destinationName(data.destination) : undefined;
  const leg = data.leg;
  if (!dest || (leg !== 'out' && leg !== 'back' && leg !== 'there')) return null;
  return `${leg}:${dest}`;
}

/** 'out:Japan' → { leg: 'out', destination: 'Japan' }; 'torn' → null. */
export function flyingOf(place: FlightPlace): { leg: FlightLegDir; destination: string } | null {
  const m = /^(out|back):(.+)$/.exec(place);
  return m && DESTINATIONS[m[2]!] ? { leg: m[1] as FlightLegDir, destination: m[2]! } : null;
}
export const abroadOf = (place: FlightPlace): string | null => {
  const m = /^there:(.+)$/.exec(place);
  return m && DESTINATIONS[m[1]!] ? m[1]! : null;
};

/** One-way flight time (ms) to a destination for a way of flying (0 when the destination isn't known). */
export const flightMs = (destination: string, type: TravelType): number => Math.round((DESTINATIONS[destination] ?? 0) * TRAVEL_TYPES[type] * 60_000);

// ---------------------------------------------------------------- timing a flight

/** For each way of flying: could it be how they flew this leg, and when they took off and land if so. */
export function legWindows(l: FlightLeg): MethodWindow[] {
  return TRAVEL_TYPE_ORDER.map((type) => {
    const d = flightMs(l.destination, type);
    // Still flying when first seen: took off less than one flight before; and after they were last seen elsewhere.
    const depLo = Math.max(l.afterAt ?? -Infinity, l.firstAt - d);
    const depHi = l.firstAt;
    // Still flying when last seen; landed by the time they were seen somewhere else.
    const landLo = Math.max(depLo + d, l.lastAt);
    const landHi = l.landedAt !== null ? Math.min(depHi + d, l.landedAt) : depHi + d;
    const fits = d > 0 && landLo <= landHi + FLIGHTS_SLACK_MS && (!l.plane || l.plane === type);
    const lo = Math.min(landLo, landHi);
    return { type, possible: fits, flightMs: d, departEarliest: lo - d, departLatest: landHi - d, landEarliest: lo, landLatest: landHi };
  });
}

/** True when a flight's timing ruled at least one way of flying out (it says something about the plane). */
export const legTimed = (l: FlightLeg): boolean => !l.plane && legWindows(l).some((w) => !w.possible);
/** True when this flight was too quick for standard (an airstrip, a private jet or business class). */
export function legFast(l: FlightLeg): boolean {
  const w = legWindows(l);
  return !w[0]!.possible && w.some((x) => x.possible);
}

/**
 * How a player usually flies, from their flights (current included): each flight whose timing ruled a way in or out votes
 * for the ways that fit it; a plane Torn's page named counts as that way. Null when no flight told anything.
 */
export function planeGuess(legs: readonly FlightLeg[]): PlaneGuess | null {
  const votes = new Map<TravelType, number>();
  let timed = 0;
  let fast = 0;
  for (const l of legs) {
    const fit = l.plane ? [l.plane] : legWindows(l).filter((w) => w.possible).map((w) => w.type);
    if (!fit.length || fit.length === TRAVEL_TYPE_ORDER.length) continue;
    timed++;
    if (!fit.includes('standard')) fast++;
    for (const t of fit) votes.set(t, (votes.get(t) ?? 0) + 1);
  }
  if (!timed) return null;
  const top = Math.max(...votes.values());
  return { types: TRAVEL_TYPE_ORDER.filter((t) => votes.get(t) === top), timed, fast, fastLikely: fast * 2 > timed };
}

/** The ways of flying worth showing first for a flight: the plane named, else the guess's (when they fit), else all that fit. */
export function likelyWindows(l: FlightLeg, guess: PlaneGuess | null): { likely: MethodWindow[]; fits: MethodWindow[] } {
  const fits = legWindows(l).filter((w) => w.possible);
  const byGuess = guess ? fits.filter((w) => guess.types.includes(w.type)) : [];
  const fast = guess?.fastLikely ? fits.filter((w) => w.type !== 'standard') : [];
  return { likely: byGuess.length ? byGuess : fast.length ? fast : fits, fits };
}

/** A flight still going at `now`: not seen landed and standard's latest landing isn't past (the slowest way). */
export const legOngoing = (l: FlightLeg, now: number): boolean => l.landedAt === null && l.firstAt + flightMs(l.destination, 'standard') + FLIGHTS_SLACK_MS >= now;

/** "Torn → Japan" / "Japan → Torn". */
export const routeText = (l: Pick<FlightLeg, 'destination' | 'leg'>): string => (l.leg === 'out' ? `Torn → ${l.destination}` : `${l.destination} → Torn`);

/** "airstrip or business class" — the guess's ways in plain words (lower case). */
export const guessText = (g: PlaneGuess): string => g.types.map((t) => TRAVEL_TYPE_LABEL[t].toLowerCase()).join(' or ');

// ---------------------------------------------------------------- reading answers (website)

const asNum = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
function readLeg(x: unknown): FlightLeg | null {
  const o = x && typeof x === 'object' ? (x as Record<string, unknown>) : null;
  if (!o || typeof o.destination !== 'string' || !DESTINATIONS[o.destination] || (o.leg !== 'out' && o.leg !== 'back')) return null;
  const firstAt = asNum(o.firstAt);
  const lastAt = asNum(o.lastAt);
  if (firstAt === null || lastAt === null) return null;
  return { destination: o.destination, leg: o.leg, afterAt: asNum(o.afterAt), firstAt, lastAt, landedAt: asNum(o.landedAt), plane: isTravelType(o.plane) ? o.plane : null };
}
function readAbroad(x: unknown): FlightPlayerAnswer['abroad'] {
  const o = x && typeof x === 'object' ? (x as Record<string, unknown>) : null;
  const since = asNum(o?.since);
  const seenAt = asNum(o?.seenAt);
  return o && typeof o.destination === 'string' && since !== null && seenAt !== null ? { destination: o.destination, since, seenAt } : null;
}
function readGuess(x: unknown): PlaneGuess | null {
  const o = x && typeof x === 'object' ? (x as Record<string, unknown>) : null;
  if (!o || !Array.isArray(o.types)) return null;
  const types = o.types.filter(isTravelType);
  return types.length ? { types, timed: asNum(o.timed) ?? 0, fast: asNum(o.fast) ?? 0, fastLikely: o.fastLikely === true } : null;
}
/** GET /api/flights/player/:id's body, checked. */
export function readFlightPlayer(body: unknown): FlightPlayerAnswer | null {
  const o = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const id = asNum(o?.id);
  if (!o || id === null) return null;
  return { id, at: asNum(o.at) ?? Date.now(), keepDays: asNum(o.keepDays) ?? FLIGHTS_KEEP_DAYS, current: readLeg(o.current), abroad: readAbroad(o.abroad), recent: Array.isArray(o.recent) ? o.recent.map(readLeg).filter((l): l is FlightLeg => !!l) : [], guess: readGuess(o.guess) };
}
/** GET /api/flights?ids='s body, checked. */
export function readFlights(body: unknown): FlightsAnswer {
  const o = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const players = Array.isArray(o?.players) ? o!.players : [];
  return {
    at: asNum(o?.at) ?? Date.now(),
    players: players.flatMap((p) => {
      const r = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
      const id = asNum(r?.id);
      return r && id !== null ? [{ id, current: readLeg(r.current), abroad: readAbroad(r.abroad), guess: readGuess(r.guess) }] : [];
    }),
  };
}
